<template>
    <div class="component-container" v-bind:class="{ inputError: hasError && isDirty }" :style="'width: '+componentWidth+'px;'">
        <croppa v-model="croppa"
            :width="width"
            :height="height"
            :initial-image="initialImage"
            :show-remove-button="false"
            :zoom-speed=20
            initial-size="contain"
            :placeholder="placeholder"
            :placeholder-font-size="placeholderFontSize"
            :prevent-white-space="false"
            :disable-drag-to-move="!enableMove"
            :disable-scroll-to-zoom="!enableZoom"
            @image-remove="deleteImage"
            @file-choose="selectImage"
            @move="updateImage"
            @zoom="updateImage"
            :disabled="disabled">
            </croppa>
        <i v-if="!disabled" @click="croppa.remove()" class="material-icons delete-icon">delete</i>
        <h4>{{label}}</h4>
        <label class="error-message">{{ isDirty ? errorText : '' }}</label>
        <div class="buttons-container" v-if="!disabled">
            <button v-if="enableRotate" @click="rotateImage()"><i class="material-icons">rotate_right</i></button>
            <button v-if="enableRotate" @click="rotateImage(-1)"><i class="material-icons">rotate_left</i></button>
            <button v-if="enableZoom" @click="croppa.zoomIn()"><i class="material-icons">zoom_in</i></button>
            <button v-if="enableZoom" @click="croppa.zoomOut()"><i class="material-icons">zoom_out</i></button>
        </div>
    </div>
</template>

<style scoped>

.component-container{
    width: max-content;
    display: inline-flex;
    flex-direction: column;
    text-align: center;
    position: relative;
}

.delete-icon{
    display: inline-flex;
    position: absolute;
    border-radius: 50%;
    z-index: 10;
    cursor: pointer;
    top: 8px;
    right: 12px;
}

i.delete-icon:hover{
    color: var(--v-error-base, red);
}

.error-message {
    color: var(--v-error-base, red);
}

.croppa-container{
    margin: auto;
    border: 2px dashed;
    border-color: #afafaf;
    border-radius: 5px;
    background-color: #fff;
}

.inputError{
    border-color: var(--v-error-base, red);
}

.buttons-container > button{
    width: 30px !important;
    height: 30px !important;
    border-radius: 25px;
    border: 1px solid;
    display: inline-flex;
    justify-content: center;
    text-align: center;
    margin: 5px;
}

</style>


<script>
export default {
    props: {
        disabled: Boolean,
        error: Boolean,
        errorCount: {
            type: [Number, String],
            default: 1
        },
        errorMessages: {
            type: [String, Array],
            default: () => []
        },
        messages: {
            type: [String, Array],
            default: () => []
        },
        readonly: Boolean,
        rules: {
            type: Array,
            default: () => []
            },
        success: Boolean,
        successMessages: {
            type: [String, Array],
            default: () => []
        },
        validateOnBlur: Boolean,
        //
        form: Object,
        imageMIME: String, 
        imageData: String, 
        imageName: String,
        //
        label: String,
        width: Number,
        height: Number,
        placeholder: String,
        'placeholder-font-size': Number,
        //
        'enable-zoom': Boolean,
        'enable-rotate': Boolean,
        'enable-move': Boolean
    },
    data() {
        return {
            croppa: undefined,
            initialImage: undefined,
            isDirty: false,
            formAssign: false,
            errorBucket: [],
            hasColor: false,
            hasFocused: false,
            hasInput: false,
            isFocused: false,
            isResetting: false,
            valid: false,
            notInitialValue: true,
        }
    },
    methods: {
        deleteImage(){
            this.$emit('update:imageMIME', null);
            this.$emit('update:imageData', null);
            this.$emit('update:imageName', null);
            this.$forceUpdate();
        },
        rotateImage(value){
            this.croppa.rotate(value);
            this.$nextTick().then(()=> {
                this.croppa.$nextTick().then(()=> {
                    setTimeout(()=> {
                        setTimeout(()=> {
                            this.croppa.moveLeftwards(1);
                        },0);
                        this.croppa.moveRightwards(1);
                    },0);
                })
            })
        },
        selectImage(value){
            this.$emit('update:imageName', value.name);
        },
        updateImage(){
            var croppaImage = this.outputImageMethod();
            var data = croppaImage.replace(/^data:([A-Za-z-+/]+);base64,/, '');
            var mime = croppaImage.replace(/^data:|(;base64,.+)/g, '');
            this.$emit('update:imageMIME', mime);
            this.$emit('update:imageData', data);
            this.isDirty = true;
        },
        // //VForm validatable
        /** @public */
        reset () {
            this.isResetting = true
            this.deleteImage();
        },
        /** @public */
        resetValidation () {
            this.isResetting = true
        },
        /** @public */
        validate (force = false, value = this.internalValue) {
            const errorBucket = []

            if (force) this.hasInput = this.hasFocused = true

            for (let index = 0; index < this.rules.length; index++) {
                const rule = this.rules[index]
                const valid = typeof rule === 'function' ? rule(value) : rule

                if (valid === false || typeof valid === 'string') {
                    errorBucket.push(valid)
                } else if (valid !== true) {
                    console.log(`Rules should return a string or boolean, received '${typeof valid}' instead`, this)
                }
            }

            this.errorBucket = errorBucket
            this.valid = errorBucket.length === 0
            return this.valid
        },
        outputImageMethod(){
            return this.croppa.generateDataUrl();
        },
    },
    mounted() {
        this.croppa.$watch('imageSet', (value) => {
            if(value){
                this.updateImage();
            }
        })
    },
    beforeMount () {
        this.validate()
    },
    created () {
        this.form && this.form.register(this);
    },

    beforeDestroy () {
        this.form && this.form.unregister(this);
    },
    computed: {
        componentWidth(){
            return this.width + 20;
        },
        deleteIconTransformX(){
            return this.width - 15;
        },
        errorText() {
            return this.errorBucket[0];
        },
        imageSrc() {
            if(this.imageMIME && this.imageData){
                return `data:${this.imageMIME};base64,${this.imageData}`;
            }else{
                return require('../assets/imagePickerDefault.png');
            }
        },
        internalValue () {
            if(this.croppa){
                var value = {
                    valid: !!this.imageMIME && !!this.imageData && !!this.imageName,
                    size: 4 * String(this.outputImageMethod()).length / 3,
                    type: this.imageMIME
                }
                return value;
            }
            return {
                valid: false,
                size: 0,
                type: ''
            };
        },
        hasError () {
            return this.internalErrorMessages.length > 0 ||
                this.errorBucket.length > 0 ||
                this.error
        },
        externalError () {
            return this.internalErrorMessages.length > 0 || this.error
        },
        // TODO: Add logic that allows the user to enable based
        // upon a good validation
        hasSuccess () {
            return this.successMessages.length > 0 ||
                this.success
        },
        hasMessages () {
            return this.validations.length > 0
        },
        hasState () {
            return this.hasSuccess || (this.shouldValidate && this.hasError)
        },
        internalErrorMessages () {
            return this.errorMessages || ''
        },
        shouldValidate () {
            return this.externalError || (!this.isResetting && (
                this.validateOnBlur
                ? this.hasFocused && !this.isFocused
                : (this.hasInput || this.hasFocused)
            ))
        },
        validations () {
            return this.validationTarget.slice(0, this.errorCount)
        },
        validationState () {
            if (this.hasError && this.shouldValidate) return 'error'
            if (this.hasSuccess) return 'success'
            if (this.hasColor) return this.color
            return null
        },
        validationTarget () {
            const target = this.internalErrorMessages.length > 0
                ? this.errorMessages
                : this.successMessages.length > 0
                ? this.successMessages
                : this.messages

            // String
            if (!Array.isArray(target)) {
                return [target]
            // Array with items
            } else if (target.length > 0) {
                return target
            // Currently has validation
            } else if (this.shouldValidate) {
                return this.errorBucket
            } else {
                return []
            }
        }
    },
    watch: {
        imageData(value) {
            this.isDirty= true;
            if(this.notInitialValue && value && value != null){
                this.initialImage = this.imageSrc;
                this.croppa.refresh();
                this.notInitialValue = false;
            }
        },
        form: {
            handler () {
                if(!this.formAssign && !!this.form){
                    this.formAssign = true;
                    this.form.register(this);
                }
            },
        },
        rules: {
            handler (newVal, oldVal) {
                if (deepEqual(newVal, oldVal)) return
                this.validate()
            },
            deep: true
        },
        internalValue () {
            // If it's the first time we're setting input,
            // mark it with hasInput
            this.hasInput = true
            this.validateOnBlur || this.$nextTick(this.validate)
        },
        isFocused (val) {
            // Should not check validation
            // if disabled or readonly
            if (
                !val &&
                !this.disabled &&
                !this.readonly
            ) {
                this.hasFocused = true
                this.validateOnBlur && this.validate()
            }
        },
        isResetting () {
            setTimeout(() => {
                this.hasInput = false
                this.hasFocused = false
                this.isResetting = false
            }, 0)
        },
        hasError (val) {
            if (this.shouldValidate) {
                this.$emit('update:error', val)
            }
        },
        hasFocused (val){
            if(val)this.isDirty = true;
        },
    }
}

export function deepEqual (a, b) {
  if (a === b) return true

  if (a instanceof Date && b instanceof Date) {
    // If the values are Date, they were convert to timestamp with getTime and compare it
    if (a.getTime() !== b.getTime()) return false
  }

  if (a !== Object(a) || b !== Object(b)) {
    // If the values aren't objects, they were already checked for equality
    return false
  }

  const props = Object.keys(a)

  if (props.length !== Object.keys(b).length) {
    // Different number of props, don't bother to check
    return false
  }

  return props.every(p => deepEqual(a[p], b[p]))
}
</script>